import React, { useCallback, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { Modal, ModalBody, Nav, Spinner } from "reactstrap";
import SimpleBar from "simplebar-react";
import Swal from "sweetalert2";
import { Button, Icon, UserAvatar } from "../../../components/Component";
import getTextColor from "../../../components/custom/getTextColor";
import { servicePutLivechatAssign } from "../../../services/livechat";
import { serviceGetUsersSalesAndCsByDeparmentId } from "../../../services/users";
import { findUpper } from "../../../utils/Utils";

const ERROR_TEXT = {
  "error.livechat.assignee_other_department": "Sales berada di department lain.",
  "error.livechat.assignee_no_access": "Sales tidak punya akses Live Chat.",
  "error.livechat.assignee_not_sales": "Hanya bisa ke Sales atau CS.",
};

const fullName = (user) => `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();

// online first, like the WhatsApp assign modal
const byStatus = (a, b) => (b.status ?? "").localeCompare(a.status ?? "");

const LivechatModalAssign = ({ isOpen, conversation, onClose, onAssigned }) => {
  const [cookies] = useCookies(["SIDikey", "departmentIds"]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const departmentIds = conversation.departmentId ?? cookies.departmentIds;

  const refresh = useCallback(async () => {
    setIsLoading(true);
    const result = await serviceGetUsersSalesAndCsByDeparmentId(cookies.SIDikey, departmentIds);
    setUsers(Array.isArray(result) ? result.filter((user) => user.isActive !== false).sort(byStatus) : []);
    setIsLoading(false);
  }, [cookies.SIDikey, departmentIds]);

  useEffect(() => {
    if (!isOpen) return;

    setSearch("");
    setSelectedId("");
    refresh();
  }, [isOpen, refresh]);

  const assign = async () => {
    const user = users.find((item) => item.id === selectedId);

    if (!user) return;

    setIsAssigning(true);
    const res = await servicePutLivechatAssign(cookies.SIDikey, conversation.conversationId, user.id);
    setIsAssigning(false);

    if (res.status === 200) {
      Swal.fire("Berhasil", `Assign Chat ke ${fullName(user)}`, "success");
      onAssigned(res.data);
      return;
    }

    Swal.fire("Gagal", ERROR_TEXT[res.data?.message] ?? "Assign Chat", "error");
  };

  const visible = users.filter((user) => fullName(user).toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <Modal isOpen={isOpen} toggle={onClose} className="modal-dialog-centered">
      <ModalBody>
        <button type="button" className="close btn btn-icon btn-trigger" aria-label="Tutup" onClick={onClose}>
          <Icon name="cross-sm" />
        </button>
        <Nav tabs>
          <Button color="transparent" disabled={isLoading} onClick={refresh}>
            Refresh
            <Icon className="ml-1" name="reload" />
          </Button>
        </Nav>
        <div className="form-control-wrap">
          <div className="form-icon form-icon-left">
            <Icon name="search" />
          </div>
          <input
            type="text"
            className="form-round form-control"
            placeholder="Search user"
            aria-label="Cari user"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <SimpleBar style={{ maxHeight: 350, minHeight: 350 }}>
          {isLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner size="sm" color="primary" />
            </div>
          ) : visible.length === 0 ? (
            <p className="mt-4">No user matched</p>
          ) : (
            visible.map((user) => {
              const isHolder = user.id === conversation.userId;
              const isSelected = user.id === selectedId;

              return (
                <div
                  key={user.id}
                  className="card-inner-md"
                  role="button"
                  tabIndex={isHolder ? -1 : 0}
                  aria-pressed={isSelected}
                  aria-disabled={isHolder}
                  style={{ cursor: isHolder ? "not-allowed" : "pointer", opacity: isHolder ? 0.5 : 1 }}
                  onClick={() => !isHolder && setSelectedId(isSelected ? "" : user.id)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !isHolder) {
                      e.preventDefault();
                      setSelectedId(isSelected ? "" : user.id);
                    }
                  }}
                >
                  <div className="user-card">
                    <Icon
                      className={isSelected ? "mr-1" : "invisible mr-1"}
                      name="check-circle-fill"
                      style={{ fontSize: 18, color: "#4461f2" }}
                    />
                    <UserAvatar theme={user.theme} text={findUpper(fullName(user))}>
                      <span className={`status dot dot-lg ${user.status === "ONLINE" ? "dot-success" : "dot-gray"}`} />
                    </UserAvatar>
                    <div className="user-info">
                      <span className="lead-text">{fullName(user)}</span>
                      <span className="sub-text">{isHolder ? "Pemegang chat" : user.email}</span>
                    </div>
                    <div className="user-action">
                      {user.departments?.map((department) => (
                        <span
                          key={department.id}
                          className="badge rounded-pill"
                          style={{
                            background: department.color || "#ffffff",
                            color: getTextColor(department.color || "#ffffff"),
                          }}
                        >
                          {department.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </SimpleBar>
        <Button
          color="primary"
          className="mt-2"
          style={{ width: "100%", justifyContent: "center" }}
          disabled={!selectedId || isAssigning}
          onClick={assign}
        >
          {isAssigning ? <Spinner size="sm" /> : "Assign"}
        </Button>
      </ModalBody>
    </Modal>
  );
};

export default LivechatModalAssign;
